import { useEffect, useState } from "react"
import { feature } from "topojson-client"
import { MAP_NAME_PROPERTY, WORLD_MAP_NAME, WORLD_MAP_URL } from "@csbi/viz-core"
import { splitAntimeridian } from "./antimeridian.js"
import { echarts, ensureEchartsRegistered } from "./echarts-setup.js"

export type WorldMap = {
  status: "idle" | "loading" | "ready" | "error"
  /** Jurisdiction codes present in the basemap. Used to decide which jurisdictions **cannot be placed** (`offMapEntries`) */
  regions: ReadonlySet<string>
  error?: string
}

const EMPTY: ReadonlySet<string> = new Set()

let cache: Promise<ReadonlySet<string>> | null = null

async function loadWorldMap(url: string): Promise<ReadonlySet<string>> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)

  const topo = (await res.json()) as Parameters<typeof feature>[0] & {
    objects?: Record<string, unknown>
  }
  const object = topo.objects?.world
  if (!object) throw new Error("the basemap has no `world` object")

  const geo = feature(topo, object as never) as unknown as { features: MapFeature[] }
  const features = splitAntimeridian(geo.features)

  ensureEchartsRegistered()
  echarts.registerMap(WORLD_MAP_NAME, { type: "FeatureCollection", features } as never)

  return regionsFrom(features)
}

export type MapFeature = {
  id?: string | number
  properties?: Record<string, unknown>
  geometry?: { type: string; coordinates: unknown } | null
}

export function regionsFrom(features: readonly MapFeature[]): ReadonlySet<string> {
  const out = new Set<string>()
  for (const f of features) {
    const key = f.properties?.[MAP_NAME_PROPERTY]
    if (typeof key === "string" && key !== "") out.add(key)
  }
  return out
}

type Outcome = { regions: ReadonlySet<string> } | { error: string }

export function useWorldMap(enabled: boolean, url: string = WORLD_MAP_URL): WorldMap {
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  useEffect(() => {
    if (!enabled) return
    let alive = true

    cache ??= loadWorldMap(url)
    cache.then(
      (regions) => {
        if (alive) setOutcome({ regions })
      },
      (err: unknown) => {
        // A failure has to be retryable: keeping the rejected promise would leave a whole session mapless after one network hiccup
        cache = null
        if (alive) setOutcome({ error: err instanceof Error ? err.message : "could not load the basemap" })
      },
    )

    return () => {
      alive = false
    }
  }, [enabled, url])

  if (outcome && "error" in outcome) return { status: "error", regions: EMPTY, error: outcome.error }
  if (outcome) return { status: "ready", regions: outcome.regions }
  return { status: enabled ? "loading" : "idle", regions: EMPTY }
}
